<script setup>
import { useRouter, useRoute } from 'vue-router';
import { usePaymentsStore } from '@/store/modules/payments';
import { useNamingPayment, NANGMAN_NAMING_SERVICE_NAME } from '@/composables/saju-insight/useNamingPayment';
import { buildNamingSelectionsFromHistoryItem } from '@/composables/saju-insight/useNamingPurchaseHistory';
import { getDailyFortuneRateAPI, postDailyFortuneRateAPI } from '@/api/calendarApi';
import { DEV_MODE } from '@/config/dev-mode';
import AnalysisMobileHeader from '@/components/analysis/AnalysisMobileHeader.vue';
import AnalysisPaymentInfoBar from '@/components/analysis/common/AnalysisPaymentInfoBar.vue';
import NamingServiceSelect from '@/components/saju-insight/nangman-naming/NamingServiceSelect.vue';
import NamingResultMobile from '@/components/saju-insight/nangman-naming/NamingResultMobile.vue';
import NamingResultDetailMobile from '@/components/saju-insight/nangman-naming/NamingResultDetailMobile.vue';
import NamingAnalyzeMobile from '@/components/saju-insight/nangman-naming/NamingAnalyzeMobile.vue';
import NamingAnalyzeDetailMobile from '@/components/saju-insight/nangman-naming/NamingAnalyzeDetailMobile.vue';
import NamingEndingMobile from '@/components/saju-insight/nangman-naming/NamingEndingMobile.vue';
import NamingResultState from '@/components/saju-insight/nangman-naming/NamingResultState.vue';
import PopRecommendCriteria from '@/components/saju-insight/nangman-naming/recommend/PopRecommendCriteria.vue';
import PopAnalyzeCriteria from '@/components/saju-insight/nangman-naming/analyze/PopAnalyzeCriteria.vue';
import PurchaseHistoryCard from '@/components/saju-insight/nangman-naming/PurchaseHistoryCard.vue';
import NamingPaymentPopup from '@/components/saju-insight/nangman-naming/NamingPaymentPopup.vue';
import ConfirmComponentPay from '@/components/saju-insight/common/confirmComponent.vue';
import AlertComponent from '@/components/common/Alert.vue';
import PopAnalysisInfo from '@/components/saju-insight/common/PopAnalysisInfo.vue';
import { formatNamingPurchaseLabel } from '@/components/saju-insight/nangman-naming/namingPurchaseLabel';
import { buildNamingInputRows } from '@/components/saju-insight/nangman-naming/namingFieldConfigs';
import { useNamingPurchaseHistory } from '@/composables/saju-insight/useNamingPurchaseHistory';
import { useNamingResult } from '@/composables/saju-insight/useNamingResult';
import { useGesture } from '@/composables/calendar/useGesture';
import coverCharacter from '@manseImages/nangman-naming/img-cover-character-mobile.png';
import { addContentLogAPI } from '@/api/logServiceApi';
import { useAnalysisSajuContext } from '@/composables/saju-insight/useAnalysisSajuContext';
import { useBackLayer, useBackReducer } from '@/composables/useAppBack';

definePage({ meta: { layout: 'm-saju-insight' } });

const router = useRouter();
const route = useRoute();
const paymentsStore = usePaymentsStore();
const context = useAnalysisSajuContext();

/** NamingServiceSelect 선택값 — 결과 페이지 전달 + 옵션 매칭(가격/티켓)에 사용 */
const namingSelections = ref({
  type: null,
  target: null,
  detail: null,
  detailInput: null,
  saju: context?.saju.value || null,
  analysisLogId: null, // 결제/기구매 식별자 — 컨텐츠 평가(objectKey)
});

/** 선택 컨텍스트 초기화 — 표지로 나가거나 새 이름 짓기 시 호출.
 *  analysisLogId가 남으면 구매내역 시트에서 직전 항목이 활성 하이라이트로 고착된다. */
const resetNamingSelections = () => {
  if (route.query.analysisLogId) {
    const query = { ...route.query };
    delete query.analysisLogId;
    delete query.menu;
    router.replace({ path: route.path, query });
  }
  namingSelections.value = {
    type: null,
    target: null,
    detail: null,
    detailInput: null,
    saju: context?.saju.value || null,
    analysisLogId: null,
  };
};

const {
  analysisTypeData,
  fetchAnalysisType,
  pointPrice,
  isPointActive,
  isTicketActive,
  namingTicketCount,
  popAnalysisType,
  submitPayment,
  isPaymentProcessing,
} = useNamingPayment(namingSelections);

// =============== 결제 상태 ===============
const showPaymentPopup = ref(false);
const showPopAnalysisInfo = ref(false);
const buyType = ref('');
const dialogConfirm = ref(null);
const dialogAlert = ref(null);
const confirmTitle = ref('');
const confirmTitle2 = ref('');
const confirmText = ref('');
const confirmNextUrl = ref(null);
const confirmDoUrl = ref(null);
const warningTxt = ref('');
const alertTitle = ref('');
const alertText2 = ref('');
const alertNextUrl2 = ref(null);

/** 현재 모드: 'cover' (표지) | 'select' (서비스 선택) | 'result' (추천 결과) */
const view = ref('cover');

const close = () => {
  addContentLogAPI({
    contentType: 'ap_naming_btn_close_opt3',
    option3: isDetailDrawerOpen.value
      ? '상세'
      : view.value === 'result'
        ? '결과'
        : view.value === 'select'
          ? '선택'
          : '표지',
  });
  if (isDetailDrawerOpen.value) {
    isDetailDrawerOpen.value = false;
    scrollResultTop();
    return;
  }
  if (view.value !== 'cover') {
    view.value = 'cover';
    resultPageIndex.value = 1;
    selectedNameIndex.value = 0;
    // 표지로 나가면 선택 컨텍스트도 초기화 — 구매내역 시트에서 직전 항목이 계속 하이라이트되는 문제 방지
    resetNamingSelections();
    scrollResultTop();
    return;
  }
  router.push(context ? `/m/saju-insight/${context.saju.value.guestUserId}` : '/saju-service');
};

const startNaming = () => {
  addContentLogAPI({ contentType: 'ap_naming_btn_start' });
  view.value = 'select';
};

const namingServiceSelectRef = ref(null);

// =============== 결과 데이터 (GET /fast/base-service, PC와 공통 composable) ===============
const {
  resultLoading,
  resultError,
  recommendList,
  criteriaRules,
  criteriaTailRows,
  analysis,
  sajuPillars,
  sajuTags,
  fetchResult,
} = useNamingResult();

/**
 * 결과 페이지 흐름 — type별 분기
 * - recommend: 1 추천 리스트 / 2 응원 메시지 / 3 평가 (상세는 드로어 노출)
 * - analyze: 1 분석 결과 / 2 카테고리 상세 / 3 응원 메시지 / 4 평가
 */
const resultPageIndex = ref(1);
const selectedNameIndex = ref(0);

const isRecommend = computed(() => namingSelections.value.type === 'recommend');
const isAnalyze = computed(() => namingSelections.value.type === 'analyze');

/** 추천 모드 — 카드 선택 시 상세 드로어 노출 (페이지네이션과 독립적) */
const isDetailDrawerOpen = ref(false);

const resultTotalPages = computed(() => (isRecommend.value ? 3 : 4));

/** 새 이름 짓기 floating 가시성
 *  추천: page 1(리스트)만 — 2·3은 응원/평가 마무리
 *  분석: page 1·2 한정 — 3·4은 응원/평가 마무리
 */
const showRestartFab = computed(() => {
  if (isRecommend.value) return resultPageIndex.value === 1 && !isDetailDrawerOpen.value;
  return resultPageIndex.value <= 2;
});

/** type별 ending subPage 매핑
 *  추천: page 2 응원, page 3 평가
 *  분석: page 3 응원, page 4 평가
 */
const endingSubPage = computed(() => {
  if (isRecommend.value) {
    if (resultPageIndex.value === 2) return 'message';
    if (resultPageIndex.value === 3) return 'survey';
  } else {
    if (resultPageIndex.value === 3) return 'message';
    if (resultPageIndex.value === 4) return 'survey';
  }
  return null;
});

/** 평가 카드 (NamingEndingMobile survey 페이지) — 낭만궁합과 동일 패턴 */
const ratingResult = ref(0);
const ratingStatus = ref(false);

const getNamingRate = async () => {
  const logId = namingSelections.value.analysisLogId;
  if (!logId) return;
  try {
    const result = await getDailyFortuneRateAPI({
      serviceName: NANGMAN_NAMING_SERVICE_NAME,
      objectKey: `${logId}`,
    });
    if (result) {
      ratingResult.value = result.score;
      ratingStatus.value = true;
    }
  } catch (e) {
    if (e?.response?.status === 404) {
      ratingResult.value = 0;
      ratingStatus.value = false;
    }
  }
};

const postNamingRate = async (score) => {
  const logId = namingSelections.value.analysisLogId;
  if (!logId) return;
  try {
    const result = await postDailyFortuneRateAPI({
      score,
      serviceName: NANGMAN_NAMING_SERVICE_NAME,
      objectKey: `${logId}`,
    });
    if (result) {
      ratingStatus.value = true;
      getNamingRate();
    }
  } catch (e) {
    DEV_MODE && console.error('[낭만작명 모바일] 평가 실패:', e);
  }
};

const onPostRate = (rate) => postNamingRate(rate);

/** 결제/기구매로 대상(analysisLogId)이 정해지면 평가 상태 리셋 후 재조회 */
watch(
  () => namingSelections.value.analysisLogId,
  () => {
    ratingResult.value = 0;
    ratingStatus.value = false;
    getNamingRate();
  },
);

const scrollResultTop = () => {
  nextTick(() => window.scrollTo({ top: 0, behavior: 'auto' }));
};

const onClickStartNaming = async () => {
  if (!namingServiceSelectRef.value?.canStart) return;
  await openPaymentPopup();
};

// =============== 결제 흐름 ===============
const openPaymentPopup = async () => {
  addContentLogAPI({ contentType: 'ap_naming_btn_start_detail' });
  await Promise.all([
    paymentsStore.getOwnPoint(),
    paymentsStore.fetchTicketCount(),
    fetchAnalysisType(),
  ]);

  // 포인트 부족 + 분석권 없음 → 포인트 충전 안내
  if (!isPointActive.value && !isTicketActive.value) {
    showPopAnalysisInfo.value = true;
    return;
  }
  // 분석권이 있으면 결제 수단 선택을 건너뛰고 바로 확인
  if (isTicketActive.value) {
    buyType.value = 'ticket';
    goConfirm();
    return;
  }
  buyType.value = 'point';
  showPaymentPopup.value = true;
};

const closePaymentPopup = () => {
  showPaymentPopup.value = false;
};

const onSelectPaymentMethod = (method) => {
  buyType.value = method;
  goConfirm();
};

const handleClosePop = () => {
  showPopAnalysisInfo.value = false;
};

const handleChargePop = (shortageAmount) => {
  showPopAnalysisInfo.value = false;
  onRecharge(shortageAmount);
};

const goConfirm = () => {
  if (!buyType.value) return;
  showPaymentPopup.value = false;

  confirmTitle.value = '선택한 결제방법으로';
  confirmTitle2.value = '낭만작명을 시작합니다.';
  confirmText.value = '사용';
  confirmNextUrl.value = onConfirmPayment;
  confirmDoUrl.value = onConfirmPayment;
  warningTxt.value =
    buyType.value === 'point'
      ? `포인트 ${pointPrice.value.toLocaleString()} P 사용`
      : '분석권 1건 사용';
  dialogConfirm.value?.openConfirmDialog();
};

const onConfirmPayment = async () => {
  if (isPaymentProcessing.value) return;
  addContentLogAPI({ contentType: 'ap_naming_payment_opt3', option3: '결제확정' });
  try {
    if (!analysisTypeData.value) await fetchAnalysisType();
    const payResult = await submitPayment({
      type: namingSelections.value.type,
      target: namingSelections.value.target,
      detail: namingSelections.value.detail,
      detailInput: namingSelections.value.detailInput,
      saju: namingSelections.value.saju,
      paymentMethod: buyType.value,
    });
    // 컨텐츠 평가(objectKey)용 analysisLogId 보관 — 신규 결제 응답에서 추출
    namingSelections.value.analysisLogId = payResult?.analysisLogId ?? null;
    DEV_MODE && console.log('[낭만작명 모바일] 결제 결과 analysisLogId:', payResult?.analysisLogId, payResult);

    alertTitle.value = '성공적으로 결제되었습니다.';
    alertText2.value = '';
    alertNextUrl2.value = onPaymentSuccess;
    dialogAlert.value?.openAlertDialog();
  } catch (e) {
    DEV_MODE && console.error('[낭만작명 모바일] 결제 실패:', e);
    alertTitle.value =
      e?.response?.data?.detail || e?.response?.data?.message || '결제에 실패했습니다.';
    alertText2.value = '';
    alertNextUrl2.value = null;
    dialogAlert.value?.openAlertDialog();
  }
};

const onPaymentSuccess = async () => {
  // 포인트(낙관적 차감 없음)·분석권 재조회 → CreditBar 갱신
  await Promise.all([paymentsStore.getOwnPoint(), paymentsStore.fetchTicketCount()]);
  // 캐시 무효화 — 다음 시트 오픈 시 활성 카테고리만 lazy fetch
  resetPurchaseCache();
  resultPageIndex.value = 1;
  selectedNameIndex.value = 0;
  isDetailDrawerOpen.value = false;
  view.value = 'result';
  // 결제 직후 결과 1페이지 진입 — 기구매 조회(onClickPurchaseCard)와 동일하게 조회 시작을 기록
  addContentLogAPI({ contentType: 'ap_naming_page_opt3', option3: '1' });
  scrollResultTop();
  // 결과 조회 — await 안 함(결과 화면의 로딩 UI가 표시됨)
  fetchResult(namingSelections.value.analysisLogId, namingSelections.value.saju);
};

const restartNaming = () => {
  addContentLogAPI({ contentType: 'ap_naming_btn_restart' });
  view.value = 'select';
  isDetailDrawerOpen.value = false;
  // 새 이름 짓기 — 이전 구매내역 선택 컨텍스트 초기화 (구매내역 카드 활성 표시 해제)
  resetNamingSelections();
  scrollResultTop();
};

const onSelectName = (idx) => {
  addContentLogAPI({ contentType: 'ap_naming_result_card_open_opt3', option3: String(idx + 1) });
  selectedNameIndex.value = idx;
  isDetailDrawerOpen.value = true;
  scrollResultTop();
};

const prevResultPage = () => {
  if (resultPageIndex.value > 1) {
    resultPageIndex.value -= 1;
    addContentLogAPI({ contentType: 'ap_naming_page_opt3', option3: String(resultPageIndex.value) });
    scrollResultTop();
  }
};

const nextResultPage = () => {
  // 로딩/에러 중에는 스와이프로도 다음 페이지 진입 차단 — 버튼 disabled와 동일 가드
  if (resultLoading.value || resultError.value) return;
  if (resultPageIndex.value < resultTotalPages.value) {
    resultPageIndex.value += 1;
    addContentLogAPI({ contentType: 'ap_naming_page_opt3', option3: String(resultPageIndex.value) });
    scrollResultTop();
  }
};

/** 결과 페이지 좌우 스와이프 — 추천 카드 상세 드로어가 열려 있으면 무시 (낭만궁합과 동일 패턴) */
const onSwipePrev = () => {
  if (isDetailDrawerOpen.value) return;
  prevResultPage();
};
const onSwipeNext = () => {
  if (isDetailDrawerOpen.value) return;
  nextResultPage();
};
const { handleStart, handleMove, handleEnd } = useGesture({
  onSwipeLeft: onSwipeNext,
  onSwipeRight: onSwipePrev,
  SWIPE_THRESHOLD: 150,
});

const formatPageNo = (n) => String(n).padStart(2, '0');

const showCriteriaPopup = ref(false);
const openCriteria = () => {
  addContentLogAPI({
    contentType: 'ap_naming_criteria_view_opt3',
    option3: isRecommend.value ? '추천기준' : '분석기준',
  });
  showCriteriaPopup.value = true;
};
const closeCriteria = () => {
  addContentLogAPI({ contentType: 'ap_naming_criteria_view_opt3', option3: '닫기' });
  showCriteriaPopup.value = false;
};

/** 추천기준보기 "작명 기준" rows — 입력값(detailInput) + 응답 꼬리행(용신오행·채점기준) */
const criteriaRowsForPopup = computed(() => {
  if (!criteriaRules.value.length) return [];
  return [
    ...buildNamingInputRows(`recommend-${namingSelections.value.target}-${namingSelections.value.detail}`, namingSelections.value.detailInput),
    ...criteriaTailRows.value,
  ];
});

/** 결과 로딩 실패 시 재시도 */
const retryFetch = () => fetchResult(namingSelections.value.analysisLogId, namingSelections.value.saju);

/** 구매한 내역 시트 */
const showPurchaseHistory = ref(false);
const openPurchaseHistory = () => {
  addContentLogAPI({ contentType: 'ap_naming_history_opt3', option3: '열기' });
  showPurchaseHistory.value = true;
};
const closePurchaseHistory = () => {
  addContentLogAPI({ contentType: 'ap_naming_history_opt3', option3: '닫기' });
  showPurchaseHistory.value = false;
};

/** 구매 내역 데이터/필터는 PC CoverPanelNaming과 공유하는 composable에서 가져온다. */
const {
  list,
  categoryPurchaseMap,
  filterByCategory,
  fetchAll: fetchAllPurchaseHistory,
  resetCache: resetPurchaseCache,
  PURCHASE_CATEGORY_TABS,
} = useNamingPurchaseHistory(() => context?.saju.value?.guestUserId);

/** 활성 카테고리 — 시트 열 때 데이터 있는 첫 카테고리로 자동 선택 */
const activePurchaseCategory = ref(PURCHASE_CATEGORY_TABS[0].key);

/** 구매 내역 목록 스크롤 컨테이너 — 카테고리 전환 시 맨 위로 되돌리기 위해 참조 */
const purchaseListRef = ref(null);

const filteredPurchaseList = computed(() => filterByCategory(activePurchaseCategory.value));

const onChangePurchaseCategory = (key) => {
  if (!categoryPurchaseMap.value[key]) return;
  addContentLogAPI({
    contentType: 'ap_naming_tab_category_opt3',
    option3: PURCHASE_CATEGORY_TABS.find((t) => t.key === key)?.label || key,
  });
  activePurchaseCategory.value = key;
  // 카테고리 전환 시 목록 스크롤을 맨 위로 초기화 (이전 카테고리 스크롤 위치 잔존 방지)
  nextTick(() => {
    if (purchaseListRef.value) purchaseListRef.value.scrollTop = 0;
  });
};

watch(showPurchaseHistory, async (visible) => {
  if (!visible) return;
  await fetchAllPurchaseHistory();
  // 현재 보고 있는(활성) 항목이 있으면 그 카테고리로 열기 (PC CoverPanelNaming과 동일)
  const activeId = namingSelections.value.analysisLogId;
  const activeItem = activeId != null
    ? list.value.find((it) => String(it.analysisLogId) === String(activeId))
    : null;
  const activeCat = activeItem?.modeKey?.split('-')[1];
  if (activeCat && categoryPurchaseMap.value[activeCat]) {
    activePurchaseCategory.value = activeCat;
  } else {
    // 활성 항목이 없으면 데이터 있는 첫 카테고리로 reset
    const firstAvailable = PURCHASE_CATEGORY_TABS.find(
      (t) => categoryPurchaseMap.value[t.key],
    );
    if (firstAvailable) activePurchaseCategory.value = firstAvailable.key;
  }
  // 시트 열 때 자동 노출되는 활성 카테고리도 탭 선택(조회)으로 기록
  if (categoryPurchaseMap.value[activePurchaseCategory.value]) {
    addContentLogAPI({
      contentType: 'ap_naming_tab_category_opt3',
      option3: PURCHASE_CATEGORY_TABS.find((t) => t.key === activePurchaseCategory.value)?.label
        || activePurchaseCategory.value,
    });
  }
});

const onClickPurchaseCard = (item) => {
  if (context && !context.isActive()) return;
  if (context && Number(item.guestUserId) !== Number(context.saju.value.guestUserId)) return;
  if (context && String(route.query.analysisLogId) !== String(item.analysisLogId)) {
    context.openHistory('naming', item.guestUserId, item.analysisLogId);
    return;
  }
  const selections = buildNamingSelectionsFromHistoryItem(item);
  if (!selections) return;
  addContentLogAPI({
    contentType: 'ap_naming_log_card_open_opt3',
    option3: `${item?.modeKey ?? ''},${item?.analysisLogId ?? ''}`,
  });
  namingSelections.value = { ...selections, saju: context?.saju.value || selections.saju };
  // 시트 닫고 결과 페이지 1로 이동 (결제 성공 후와 동일 흐름)
  showPurchaseHistory.value = false;
  resultPageIndex.value = 1;
  selectedNameIndex.value = 0;
  isDetailDrawerOpen.value = false;
  view.value = 'result';
  // 기구매 항목 결과 1페이지 진입 (log_card_open과 별개로 결과 조회 시작을 통일 기록)
  addContentLogAPI({ contentType: 'ap_naming_page_opt3', option3: '1' });
  scrollResultTop();
  // 기구매 항목 결과 재조회
  fetchResult(selections.analysisLogId, namingSelections.value.saju);
};

/** 보유 작명분석권 총합 — 표지 뱃지/결제바용 (선택 옵션과 무관한 전체 보유량).
 *  옵션별 보유수(useNamingPayment의 namingTicketCount)는 결제 판단·결제 팝업에만 사용한다.
 *  결과·구매내역 조회로 옵션이 채워진 채 표지로 돌아와도 0건으로 떨어지지 않도록 총합을 직접 참조한다. */
const ticketCount = computed(() => paymentsStore.namingTicketCount ?? 0);

const onRecharge = (shortageAmount) => {
  router.push({ path: '/purchase/points', query: { next: route.fullPath, shortageAmount } });
};

const unwindNamingOne = () => {
  if (isDetailDrawerOpen.value) {
    isDetailDrawerOpen.value = false;
    scrollResultTop();
    return;
  }
  if (view.value === 'result' && resultPageIndex.value > 1) {
    prevResultPage();
    return;
  }
  if (view.value === 'result') {
    view.value = 'select';
    scrollResultTop();
    return;
  }
  if (view.value === 'select') {
    view.value = 'cover';
    resultPageIndex.value = 1;
    selectedNameIndex.value = 0;
    resetNamingSelections();
    scrollResultTop();
  }
};

const restoreHistory = async () => {
  if (!route.query.analysisLogId) return;
  await fetchAllPurchaseHistory();
  if (context && !context.isActive()) return;
  const item = list.value.find((entry) => String(entry.analysisLogId) === String(route.query.analysisLogId));
  if (item) onClickPurchaseCard(item);
};
watch(() => route.query.analysisLogId, restoreHistory);

onMounted(async () => {
  await Promise.all([
    paymentsStore.getOwnPoint(),
    paymentsStore.fetchTicketCount(),
    fetchAnalysisType(),
  ]);
  await restoreHistory();
});

useBackLayer({
  id: 'naming-payment',
  isOpen: showPaymentPopup,
  closeFromBack: closePaymentPopup,
});
useBackLayer({
  id: 'naming-criteria',
  isOpen: showCriteriaPopup,
  closeFromBack: closeCriteria,
});
useBackLayer({
  id: 'naming-purchase-history',
  isOpen: showPurchaseHistory,
  closeFromBack: closePurchaseHistory,
});
useBackReducer({
  id: 'naming-flow',
  canUnwind: () => isDetailDrawerOpen.value || view.value !== 'cover',
  getDepth: () => {
    const detailDepth = isDetailDrawerOpen.value ? 1 : 0;
    if (view.value === 'result') return detailDepth + resultPageIndex.value + 1;
    if (view.value === 'select') return detailDepth + 1;
    return detailDepth;
  },
  unwindOne: unwindNamingOne,
});
</script>

<template>
  <div class="pop-saju-reading-container mobile st-analysis-naming">
    <!-- 헤더 (레이아웃에서 position: sticky 적용됨) -->
    <AnalysisMobileHeader title="낭만작명" :show-person-icon="false" @close="close" />

    <!-- 본문: 표지 -->
    <div v-if="view === 'cover'" class="naming-mobile-cover">
      <div class="naming-mobile-cover__inner">
        <!-- 타이틀 + 배지 -->
        <div class="naming-mobile-cover__head">
          <div class="naming-mobile-cover__title">
            <p class="naming-mobile-cover__sub">단 하나의 의미 있는 이름</p>
            <p class="naming-mobile-cover__service">낭만 작명</p>
          </div>
          <div class="naming-mobile-cover__badge">
            <span class="naming-mobile-cover__badge-tag">보유 작명분석권</span>
            <span class="naming-mobile-cover__badge-count">{{ ticketCount }}건</span>
          </div>
        </div>

        <!-- 캐릭터 일러스트 -->
        <div class="naming-mobile-cover__img">
          <img :src="coverCharacter" alt="낭만작명" />
        </div>

        <!-- 인트로 카드 -->
        <div class="naming-mobile-cover__intro">
          <p class="naming-mobile-cover__intro-heading">오래 불릴 이름에는 이유가 있으니까</p>
          <div class="naming-mobile-cover__intro-card">
            <p>
              이름은 단순한 호칭을 넘어, 마음과 의미를 담아 오래 함께하는 존재가 됩니다.
              누군가를 부르는 한마디 안에는 바라는 마음과 그 존재만의 분위기가 자연스럽게 스며들어 있죠.
            </p>
            <p>
              낭만작명은 사주의 흐름과 원하는 이미지, 담고 싶은 의미를 바탕으로 사람의 이름은 물론 반려 존재와 브랜드, 아호까지 자연스럽게 어울리는 이름을 함께 찾아갑니다.
            </p>
            <p>오래도록 편안하게 불리고, 시간이 지나도 의미 있게 남을 이름을 만나보세요.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 본문: 서비스 선택 -->
    <div v-else-if="view === 'select'" class="naming-mobile-select">
      <NamingServiceSelect
        ref="namingServiceSelectRef"
        :fixed-saju="context?.saju.value"
        mobile
        @update:selected-type="namingSelections.type = $event"
        @update:selected-target="namingSelections.target = $event"
        @update:selected-detail="namingSelections.detail = $event"
        @update:detail-input="namingSelections.detailInput = $event"
        @update:selected-saju="namingSelections.saju = $event"
      />
    </div>

    <!-- 본문: 결과 — type별 페이지 분기
         recommend: 1 리스트 / 2 상세 / 3 응원 / 4 평가
         analyze: 1 분석 / 2 응원 / 3 평가 -->
    <div
      v-else-if="view === 'result'"
      class="naming-mobile-result"
      @mousedown="handleStart"
      @mousemove="handleMove"
      @mouseup="handleEnd"
      @touchstart.passive="handleStart"
      @touchmove.passive="handleMove"
      @touchend.passive="handleEnd"
    >
      <!-- 결과 본문(page 1) 로딩/에러 — 추천은 LLM 동기 호출로 최대 ~2분 -->
      <NamingResultState
        v-if="(resultLoading || resultError) && resultPageIndex === 1 && !isDetailDrawerOpen"
        :loading="resultLoading"
        :error="resultError"
        :long="isRecommend"
        @retry="retryFetch"
      />
      <template v-else>
        <NamingResultMobile
          v-if="isRecommend && resultPageIndex === 1 && !isDetailDrawerOpen"
          :target="namingSelections.target"
          :detail="namingSelections.detail"
          :detail-input="namingSelections.detailInput"
          :saju="namingSelections.saju"
          :recommend-list="recommendList"
          :summary="[]"
          @open-criteria="openCriteria"
          @select-name="onSelectName"
        />
        <NamingResultDetailMobile
          v-else-if="isRecommend && isDetailDrawerOpen"
          :target="namingSelections.target"
          :detail="namingSelections.detail"
          :detail-input="namingSelections.detailInput"
          :saju="namingSelections.saju"
          :recommend-list="recommendList"
          :selected-index="selectedNameIndex"
        />
        <NamingAnalyzeMobile
          v-else-if="isAnalyze && resultPageIndex === 1 && analysis"
          :target="namingSelections.target"
          :detail="namingSelections.detail"
          :detail-input="namingSelections.detailInput"
          :saju="namingSelections.saju"
          :analysis="analysis"
          @open-criteria="openCriteria"
        />
        <NamingAnalyzeDetailMobile
          v-else-if="isAnalyze && resultPageIndex === 2 && analysis"
          :target="namingSelections.target"
          :detail="namingSelections.detail"
          :detail-input="namingSelections.detailInput"
          :saju="namingSelections.saju"
          :analysis="analysis"
        />
        <NamingEndingMobile
          v-else-if="endingSubPage"
          :type="namingSelections.type || 'recommend'"
          :sub-page="endingSubPage"
          :rating-result="ratingResult"
          :rating-status="ratingStatus"
          @post-rate="onPostRate"
          @go-to-over-view="restartNaming"
        />
      </template>
    </div>

    <!-- 표지 — 시작 버튼 + 결제정보 바 -->
    <template v-if="view === 'cover'">
      <div class="btn-footer-box">
        <button type="button" class="btn-white" @click="startNaming">
          낭만작명 시작하기 →
        </button>
      </div>
      <AnalysisPaymentInfoBar
        ticket-label="작명분석권"
        :ticket-count="ticketCount"
        :formatted-points="paymentsStore?.formattedPoints"
        @recharge="onRecharge"
      />
    </template>

    <!-- 서비스 선택 — 구매한 내역 + 시작하기 split 바 (낭만궁합 동일) -->
    <div v-else-if="view === 'select'" class="naming-bottom-bar">
      <button type="button" class="naming-bottom-bar__log" @click="openPurchaseHistory">
        구매한 내역
      </button>
      <button
        type="button"
        class="naming-bottom-bar__start"
        :class="{ active: namingServiceSelectRef?.canStart }"
        :disabled="!namingServiceSelectRef?.canStart"
        @click="onClickStartNaming"
      >
        낭만작명 시작하기 →
      </button>
    </div>

    <!-- 결과 — floating '새 이름 짓기'(page 1·2 한정) + 구매한 내역 + 페이지 네비 split 바 -->
    <template v-else-if="view === 'result'">
      <button
        v-if="showRestartFab"
        type="button"
        class="naming-restart-fab"
        @click="restartNaming"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M13.3333 8C13.3333 10.9455 10.9455 13.3333 8 13.3333C5.05448 13.3333 2.66667 10.9455 2.66667 8C2.66667 5.05448 5.05448 2.66667 8 2.66667C9.55048 2.66667 10.9456 3.32935 11.9171 4.38755" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M12 2V4.66667H9.33333" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        새 이름 짓기
      </button>

      <div v-show="!isDetailDrawerOpen" class="naming-bottom-bar">
        <button type="button" class="naming-bottom-bar__log" @click="openPurchaseHistory">
          구매한 내역
        </button>
        <div class="naming-bottom-bar__pager">
          <button
            type="button"
            class="naming-bottom-bar__pager-btn"
            :disabled="resultPageIndex <= 1"
            aria-label="이전 페이지"
            @click="prevResultPage"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M11.25 13.5L6.75 9L11.25 4.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
          <span class="naming-bottom-bar__pager-text">
            {{ formatPageNo(resultPageIndex) }} / {{ formatPageNo(resultTotalPages) }}
          </span>
          <button
            type="button"
            class="naming-bottom-bar__pager-btn"
            :disabled="resultPageIndex >= resultTotalPages || resultLoading || !!resultError"
            aria-label="다음 페이지"
            @click="nextResultPage"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6.75 4.5L11.25 9L6.75 13.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
        </div>
      </div>
    </template>

    <!-- 추천 기준 / 분석 기준 모달 — type에 따라 분기 -->
    <PopRecommendCriteria
      v-if="isRecommend"
      :is-visible="showCriteriaPopup"
      :saju="{ pillars: sajuPillars, tags: sajuTags }"
      :criteria="{ rows: criteriaRowsForPopup }"
      :rules="criteriaRules"
      @close="closeCriteria"
    />
    <PopAnalyzeCriteria
      v-else-if="isAnalyze"
      :is-visible="showCriteriaPopup"
      :saju="{ pillars: sajuPillars, tags: sajuTags }"
      @close="closeCriteria"
    />

    <!-- 구매한 내역 바텀시트 — 낭만궁합 .harmony-sheet 패턴 동일 -->
    <Teleport to="body">
      <Transition name="naming-sheet-fade">
        <div
          v-if="showPurchaseHistory"
          class="naming-purchase-sheet-backdrop"
          @click.self="closePurchaseHistory"
        >
          <Transition name="naming-sheet-slide">
            <div v-if="showPurchaseHistory" class="naming-purchase-sheet">
              <div class="naming-purchase-sheet__handle"/>
              <div class="naming-purchase-sheet__header">
                <span class="naming-purchase-sheet__title">구매한 내역</span>
                <button
                  type="button"
                  class="naming-purchase-sheet__close"
                  aria-label="닫기"
                  @click="closePurchaseHistory"
                />
              </div>
              <div class="naming-purchase-sheet__divider"/>
              <div class="naming-purchase-sheet__tabs">
                <button
                  v-for="tab in PURCHASE_CATEGORY_TABS"
                  :key="tab.key"
                  type="button"
                  class="naming-purchase-sheet__tab"
                  :class="{
                    active: activePurchaseCategory === tab.key && categoryPurchaseMap[tab.key],
                    'naming-purchase-sheet__tab--disabled': !categoryPurchaseMap[tab.key],
                  }"
                  :disabled="!categoryPurchaseMap[tab.key]"
                  @click="onChangePurchaseCategory(tab.key)"
                >
                  {{ tab.label }}
                </button>
              </div>
              <div ref="purchaseListRef" class="naming-purchase-sheet__list">
                <PurchaseHistoryCard
                  v-for="item in filteredPurchaseList"
                  :key="item.id"
                  :saju-name="item.sajuName"
                  :selection-info="formatNamingPurchaseLabel(item.modeKey, item.detailInput)"
                  :purchase-date="item.purchaseDate"
                  :valid-until="item.validUntil"
                  :active="namingSelections.analysisLogId != null && String(item.analysisLogId) === String(namingSelections.analysisLogId)"
                  @click="onClickPurchaseCard(item)"
                />
                <div v-if="!filteredPurchaseList.length" class="naming-purchase-sheet__empty">
                  구매내역이 없습니다
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </Transition>
    </Teleport>

    <!-- 결제 수단 선택 팝업 -->
    <NamingPaymentPopup
      :is-visible="showPaymentPopup"
      :analysis-type="popAnalysisType"
      :ticket-count="namingTicketCount"
      :is-point-active="isPointActive"
      :is-ticket-active="isTicketActive"
      @close="closePaymentPopup"
      @select-method="onSelectPaymentMethod"
    />

    <!-- 결제 확인 다이얼로그 -->
    <ConfirmComponentPay
      ref="dialogConfirm"
      :title="confirmTitle"
      :title2="confirmTitle2"
      :text="confirmText"
      :warning="warningTxt"
      :next-url="confirmNextUrl"
      :do-url="confirmDoUrl"
    />

    <!-- 결제 결과 알림 -->
    <AlertComponent
      ref="dialogAlert"
      :title="alertTitle"
      :text="alertText2"
      :next-url="alertNextUrl2"
    />

    <!-- 포인트 부족 + 분석권 없음 → 포인트 충전 안내 -->
    <Teleport to="body">
      <PopAnalysisInfo
        :is-visible="showPopAnalysisInfo"
        :analysis-type="popAnalysisType"
        @close="handleClosePop"
        @charge="handleChargePop"
      />
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
$naming-accent: #5e47a6;
$naming-bg: #f8f6ff;

/* 컨테이너 — 문서 레벨 스크롤 (m-saju-insight 레이아웃 정책) */
.pop-saju-reading-container.mobile.st-analysis-naming {
  height: auto;
  overflow: visible;
  background: #fff;
}

/* 본문 — 표지: 시작 버튼(46px) + 페이먼트 바(46px) padding-bottom 확보 */
.naming-mobile-cover {
  background: $naming-bg;
  padding-bottom: calc(140px + var(--safe-area-bottom, 0px));
  min-height: calc(100vh - 52px - var(--safe-area-top, 0px));
  box-sizing: border-box;
}

/* 본문 — 서비스 선택: split 바(52px) 만큼 padding-bottom */
.naming-mobile-select {
  background: #fff;
  padding-bottom: calc(52px + var(--safe-area-bottom, 0px));
  min-height: calc(100vh - 52px - var(--safe-area-top, 0px));
  box-sizing: border-box;
}

/* 본문 — 추천 결과: split 바(52px) 만큼 padding-bottom */
.naming-mobile-result {
  background: #fff;
  padding-bottom: calc(52px + var(--safe-area-bottom, 0px));
  min-height: calc(100vh - 52px - var(--safe-area-top, 0px));
  box-sizing: border-box;
}

/* 시작 버튼 — 페이먼트 바(높이 46px) 바로 위에 고정 */
.btn-footer-box {
  position: fixed;
  bottom: calc(46px + var(--safe-area-bottom, 0px));
  left: 0;
  right: 0;
  padding: 16px;
  z-index: 10;
}

.btn-white {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  height: 48px;
  padding: 14px 20px;
  border-radius: 6px;
  border: 1px solid $naming-accent;
  background: #fff;
  color: $naming-accent;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
  box-shadow: 0 -10px 10px rgba(0, 0, 0, 0.05);
  cursor: pointer;
}

.naming-mobile-cover__inner {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 30px 0 20px;
  align-items: center;
}

/* 헤더 */
.naming-mobile-cover__head {
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: flex-start;
  width: 100%;
  padding: 0 16px;
  box-sizing: border-box;
}

.naming-mobile-cover__title {
  display: flex;
  flex-direction: column;
  color: #333;
  font-family: Pretendard;
  line-height: 44px;
}

.naming-mobile-cover__sub {
  margin: 0;
  font-size: 28px;
  font-weight: 400;
}

.naming-mobile-cover__service {
  margin: 0;
  font-size: 32px;
  font-weight: 700;
  color: $naming-accent;
}

.naming-mobile-cover__badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.naming-mobile-cover__badge-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  border-radius: 18px;
  background: $naming-accent;
  color: #fff;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
}

.naming-mobile-cover__badge-count {
  color: #333;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
}

/* 일러스트 */
.naming-mobile-cover__img {
  width: 100%;

  img {
    width: 100%;
    height: auto;
    display: block;
    object-fit: contain;
  }
}

/* 인트로 카드 */
.naming-mobile-cover__intro {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  padding: 0 16px;
  box-sizing: border-box;
}

.naming-mobile-cover__intro-heading {
  margin: 0;
  padding: 0 12px;
  color: $naming-accent;
  font-family: Pretendard;
  font-size: 16px;
  font-weight: 700;
  line-height: 26px;
}

.naming-mobile-cover__intro-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  border-radius: 12px;
  background: #fff;

  p {
    margin: 0;
    color: #333;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
  }
}

/* 서비스 선택 하단 split 바 — 구매한 내역 + 시작하기 */
.naming-bottom-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: stretch;
  height: calc(52px + var(--safe-area-bottom, 0px));
  padding-bottom: var(--safe-area-bottom, 0px);
  background: #333;
  z-index: 10;
  box-sizing: border-box;
}

.naming-bottom-bar__log {
  flex: 0 0 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px 16px;
  background: #eb4c10;
  color: #fff;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  border: none;
  cursor: pointer;
}

.naming-bottom-bar__start {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px 20px;
  background: #c2c2c2;
  color: #fff;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  border: none;
  cursor: not-allowed;

  &.active {
    background: #333;
    cursor: pointer;
  }
}

/* 결과 — 페이지 네비 영역 (구매한 내역 오른쪽) */
.naming-bottom-bar__pager {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 14px 20px;
  background: #333;
  color: #fff;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  box-sizing: border-box;
}

.naming-bottom-bar__pager-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;

  &:disabled {
    opacity: 0.3;
    cursor: default;
  }
}

.naming-bottom-bar__pager-text {
  color: #fff;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  text-align: center;
}

/* 결과 — "새 이름 짓기" floating 액션 (split 바 위에 위치) */
.naming-restart-fab {
  position: fixed;
  right: 16px;
  bottom: calc(52px + 16px + var(--safe-area-bottom, 0px));
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 10px 12px;
  border: none;
  border-radius: 60px;
  background: #714ce5;
  color: #fff;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  box-shadow: 2px 2px 5px rgba(113, 76, 229, 0.2);
  cursor: pointer;
  z-index: 11;
}
</style>

<!-- 구매한 내역 바텀시트: Teleport to=body라 scoped 회피, 글로벌 스타일로 정의.
     낭만궁합 .harmony-sheet 스타일과 동일 톤 (탭 없는 단순 리스트) -->
<style lang="scss">
.naming-purchase-sheet-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 9998;
}

.naming-purchase-sheet {
  width: 100%;
  max-width: 500px;
  height: calc(100vh - 60px);
  height: calc(100dvh - 60px);
  background: #fff;
  border-radius: 20px 20px 0 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &__handle {
    width: 40px;
    height: 4px;
    border-radius: 2px;
    background: #c2c2c2;
    margin: 12px auto 0;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px 8px;
  }

  &__title {
    color: #eb4c10;
    font-family: Pretendard;
    font-size: 18px;
    font-weight: 700;
    line-height: 24px;
  }

  &__close {
    width: 24px;
    height: 24px;
    background: url('@manseImages/icon_close_1.svg') center/24px no-repeat;
    border: none;
    cursor: pointer;
    padding: 0;
  }

  &__divider {
    height: 2px;
    width: 76px;
    background: #eb4c10;
    margin: 0 0 0 20px;
  }

  &__tabs {
    display: flex;
    gap: 8px;
    padding: 16px 20px 12px;
    flex-shrink: 0;
    width: 100%;
    box-sizing: border-box;
  }

  &__tab {
    display: flex;
    flex: 1 1 0;
    min-width: 0;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 10px 6px;
    border-radius: 100px;
    border: 1px solid #ebebeb;
    background: #f8f8f8;
    color: #757575;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    white-space: nowrap;
    cursor: pointer;

    &.active {
      border-color: #eb4c10;
      color: #eb4c10;
      font-weight: 700;
      background: #fff8f5;
    }

    &--disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__list {
    flex: 1;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 16px 20px 24px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    /* 모바일 바텀시트 목록 스크롤바 숨김 */
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE/Edge */

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 40px 20px;
    border: 1px solid #ebebeb;
    border-radius: 10px;
    background: #fff;
    color: #333;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
  }
}

/* 바텀시트 트랜지션 — 낭만궁합과 동일 */
.naming-sheet-fade-enter-active,
.naming-sheet-fade-leave-active {
  transition: opacity 0.25s ease;
}
.naming-sheet-fade-enter-from,
.naming-sheet-fade-leave-to {
  opacity: 0;
}

.naming-sheet-slide-enter-active {
  transition: transform 0.3s ease-out;
}
.naming-sheet-slide-leave-active {
  transition: transform 0.25s ease-in;
}
.naming-sheet-slide-enter-from,
.naming-sheet-slide-leave-to {
  transform: translateY(100%);
}
</style>
